import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '@terminal/components/theme-controller/utils/theme-utils/theme-utils';

import { copyCanvasStylesToImage, getCanvasDataUrl } from './fetchStylesUtils';

const ACCORDION_EXPAND_DELAY_MS = 500;

interface CanvasImageSwap {
  canvas: HTMLCanvasElement;
  img: HTMLImageElement;
}

interface WithExpandedDrawerAccordionsArgs<T> {
  expandAccordions?: (expand: boolean) => void;
  run: () => Promise<T>;
}

const canvasToImage = (
  canvas: HTMLCanvasElement,
  shouldInvert: boolean
): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = getCanvasDataUrl(canvas, shouldInvert);
  copyCanvasStylesToImage(canvas, img);
  return img;
};

export const replaceCanvasesWithImages = (root: HTMLElement): (() => void) => {
  const canvasImages: CanvasImageSwap[] = [];
  const shouldInvert = isDarkTheme(useThemeStore.getState().themeName);

  root.querySelectorAll('canvas').forEach(canvas => {
    const img = canvasToImage(canvas, shouldInvert);
    canvas.style.display = 'none';
    canvas.parentNode?.insertBefore(img, canvas);
    canvasImages.push({ canvas, img });
  });

  return () => {
    canvasImages.forEach(({ canvas, img }) => {
      canvas.style.display = '';
      img.remove();
    });
  };
};

export const transferCanvasesToClone = (
  source: HTMLElement,
  clone: HTMLElement
) => {
  const shouldInvert = isDarkTheme(useThemeStore.getState().themeName);
  const sourceCanvases = source.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');

  sourceCanvases.forEach((canvas, index) => {
    const clonedCanvas = clonedCanvases[index];
    if (!clonedCanvas) {
      return;
    }

    clonedCanvas.parentNode?.replaceChild(
      canvasToImage(canvas, shouldInvert),
      clonedCanvas
    );
  });
};

export const withExpandedDrawerAccordions = async <T>({
  expandAccordions,
  run,
}: WithExpandedDrawerAccordionsArgs<T>): Promise<T> => {
  if (!expandAccordions) {
    return run();
  }

  expandAccordions(true);
  try {
    await new Promise(resolve =>
      setTimeout(resolve, ACCORDION_EXPAND_DELAY_MS)
    );
    return await run();
  } finally {
    expandAccordions(false);
  }
};
